import React, { useState, useEffect } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from './ui/button';

export default function UpdateBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  // Helpers for the "Later / X dismissal must stick" behavior. We key the
  // dismissal on the waiting worker's script URL so a brand-new deploy
  // (which serves a new service-worker.js) bypasses the dismissal and the
  // banner appears once. Without this, every page navigation re-runs the
  // useEffect, sees `registration.waiting`, and re-shows the banner —
  // exactly the "I clicked Later and it came back" symptom.
  const dismissKey = (workerScriptURL) => `fr_update_dismissed:${workerScriptURL || 'unknown'}`;
  const isDismissed = (worker) => {
    try { return worker && localStorage.getItem(dismissKey(worker.scriptURL)) === '1'; }
    catch { return false; }
  };
  const markDismissed = (worker) => {
    try { if (worker) localStorage.setItem(dismissKey(worker.scriptURL), '1'); }
    catch { /* localStorage unavailable — best-effort */ }
  };

  useEffect(() => {
    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) return;

    const registerSW = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/service-worker.js');

        // Guard: only show the banner if there is actually a worker still
        // in the 'waiting' state AND the user hasn't already dismissed it.
        // After a fast `skipWaiting()` activate, `registration.waiting` is
        // null even though `controllerchange` may not have fired yet —
        // that's the race that left users with a banner whose "Update Now"
        // button did nothing on the previous deploys.
        if (registration.waiting && !isDismissed(registration.waiting)) {
          setWaitingWorker(registration.waiting);
          setShowBanner(true);
        }

        // Listen for new service worker installing
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;

          newWorker.addEventListener('statechange', () => {
            // When the new worker is installed and waiting AND the user
            // hasn't already dismissed THIS specific worker. The dismissal
            // key is the worker's scriptURL so a future deploy (different
            // script) gets a clean banner.
            if (newWorker.state === 'installed'
                && navigator.serviceWorker.controller
                && !isDismissed(newWorker)) {
              setWaitingWorker(newWorker);
              setShowBanner(true);
            }
          });
        });

        // Check for updates every 5 minutes
        setInterval(() => {
          registration.update();
        }, 5 * 60 * 1000);

        // Also check on page visibility change (when user returns to app)
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            registration.update();
          }
        });

      } catch (error) {
        console.error('Service worker registration failed:', error);
      }
    };

    // Listen for controller change (when new SW takes over)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });

    registerSW();
  }, []);

  const handleUpdate = () => {
    // Tell the waiting worker to skip waiting and activate. The
    // controllerchange listener above will then reload the page so the
    // user lands on the new bundle.
    //
    // Belt-and-suspenders: if the worker is no longer in 'waiting'
    // (race condition — it may have auto-activated between render and
    // click), the postMessage is a no-op and controllerchange will never
    // fire. In that case force a reload manually so the click is never
    // a dead end. The page will pick up the new bundle either way.
    if (waitingWorker) {
      try { waitingWorker.postMessage({ type: 'SKIP_WAITING' }); } catch { /* worker gone */ }
    }
    setShowBanner(false);
    // Small delay so the SKIP_WAITING message has a chance to dispatch
    // before we reload — if it lands first, controllerchange triggers
    // its own reload and this one is harmless.
    setTimeout(() => { window.location.reload(); }, 250);
  };

  const handleDismiss = () => {
    // Persist the dismissal so the banner doesn't pop back on every
    // page navigation. Keyed on the worker's scriptURL — a future deploy
    // (new script URL) bypasses this and shows the banner fresh.
    markDismissed(waitingWorker);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-blue-600 text-white rounded-lg shadow-lg p-4 z-50 animate-in slide-in-from-bottom duration-300">
      <div className="flex items-start gap-3">
        <RefreshCw className="w-5 h-5 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-semibold">Update Available</p>
          <p className="text-sm text-blue-100 mt-1">
            A new version of FieldReady is ready. Tap to update now.
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleUpdate}
              className="bg-white text-blue-600 hover:bg-blue-50"
            >
              Update Now
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="text-white hover:bg-blue-500"
            >
              Later
            </Button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-blue-200 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
